// Pure poker logic utility functions for PocketPoker V2

/**
 * Assign roles to players at the start of a new hand.
 * @param {Array} players - List of player objects
 * @param {string|null} currentDealerId - Current dealer's ID (null for first hand)
 * @returns {Object} Object with roleAssignments and nextDealerId
 */
export function assignRoles(players, currentDealerId = null) {
  // Only consider players with chips for role assignments
  const playersWithChips = players.filter(p => p.stack > 0);
  
  if (playersWithChips.length < 2) {
    throw new Error('Need at least 2 players with chips to assign roles');
  }

  let dealerIndex = 0;
  
  // If there's a current dealer, find the next dealer among players with chips
  if (currentDealerId) {
    const currentDealerIndex = playersWithChips.findIndex(p => p.id === currentDealerId);
    if (currentDealerIndex !== -1) {
      dealerIndex = (currentDealerIndex + 1) % playersWithChips.length;
    } else {
      // Current dealer is busted, find their position in original list and move to next player with chips
      const originalDealerIndex = players.findIndex(p => p.id === currentDealerId);
      if (originalDealerIndex !== -1) {
        // Find the next player with chips after the current dealer
        for (let i = 1; i <= players.length; i++) {
          const nextIndex = (originalDealerIndex + i) % players.length;
          const nextPlayer = players[nextIndex];
          if (nextPlayer.stack > 0) {
            dealerIndex = playersWithChips.findIndex(p => p.id === nextPlayer.id);
            break;
          }
        }
      }
    }
  }

  const roleAssignments = {};
  
  // Initialize all players as 'PLAYER' first
  for (const player of players) {
    roleAssignments[player.id] = 'PLAYER';
  }
  
  if (playersWithChips.length === 2) {
    // Heads-up rules: Dealer is also small blind
    roleAssignments[playersWithChips[dealerIndex].id] = 'DEALER_SMALL_BLIND';
    roleAssignments[playersWithChips[(dealerIndex + 1) % playersWithChips.length].id] = 'BIG_BLIND';
  } else {
    // Normal game: Dealer, Small Blind, Big Blind are separate
    roleAssignments[playersWithChips[dealerIndex].id] = 'DEALER';
    roleAssignments[playersWithChips[(dealerIndex + 1) % playersWithChips.length].id] = 'SMALL_BLIND';
    roleAssignments[playersWithChips[(dealerIndex + 2) % playersWithChips.length].id] = 'BIG_BLIND';
  }

  return {
    roleAssignments,
    nextDealerId: playersWithChips[dealerIndex].id,
  };
}

/**
 * Calculate player positions relative to the dealer button for remainder chip distribution.
 * Position 0 = dealer, position 1 = small blind (first left of dealer), etc.
 * Used for Texas Hold'em remainder chip distribution rules.
 * 
 * @param {Array} players - Array of active player objects
 * @param {string} dealerId - Current dealer ID
 * @returns {Object} Map of playerId to position number
 */
export function calculatePlayerPositions(players, dealerId) {
  const activePlayers = players.filter(p => p.isActive && p.stack > 0);
  
  if (activePlayers.length === 0) {
    return {};
  }
  
  // Find dealer index
  const dealerIndex = activePlayers.findIndex(p => p.id === dealerId);
  
  if (dealerIndex === -1) {
    console.warn('Dealer not found in active players, using first player as reference');
    // Fallback: assign positions starting from first active player
    const positions = {};
    activePlayers.forEach((player, index) => {
      positions[player.id] = index;
    });
    return positions;
  }
  
  // Calculate positions relative to dealer (clockwise from dealer)
  const positions = {};
  
  for (let i = 0; i < activePlayers.length; i++) {
    const playerIndex = (dealerIndex + i) % activePlayers.length;
    const player = activePlayers[playerIndex];
    positions[player.id] = i; // Position 0 = dealer, 1 = small blind, etc.
  }
  
  console.log('🎯 PLAYER POSITIONS:', positions);
  return positions;
}

/**
 * Get the first player to act after blinds are posted.
 * @param {Array} players - List of player objects with roles assigned
 * @returns {string|null} Player ID of first to act, or null if error
 */
export function getFirstToAct(players) {
  // Only consider players with chips for acting (stack > 0 because $0 players cannot bet)
  const playersWithChips = players.filter(p => p.stack > 0);
  
  if (playersWithChips.length < 2) return null;

  if (playersWithChips.length === 2) {
    // Heads-up: Small blind (dealer) acts first pre-flop
    const smallBlindPlayer = playersWithChips.find(p => p.role === 'DEALER_SMALL_BLIND');
    return smallBlindPlayer ? smallBlindPlayer.id : null;
  } else {
    // Normal game: Player after big blind acts first (among players with chips)
    const bigBlindPlayer = playersWithChips.find(p => p.role === 'BIG_BLIND');
    if (!bigBlindPlayer) return null;
    
    const bigBlindIndex = playersWithChips.findIndex(p => p.id === bigBlindPlayer.id);
    const firstToActIndex = (bigBlindIndex + 1) % playersWithChips.length;
    return playersWithChips[firstToActIndex].id;
  }
}

/**
 * Calculate blind amounts for players.
 * @param {Array} players - List of player objects with roles
 * @param {number} smallBlind - Small blind amount
 * @param {number} bigBlind - Big blind amount
 * @returns {Object} Object with playerId -> blindAmount mapping
 */
export function calculateBlinds(players, smallBlind, bigBlind) {
  const blindAmounts = {};

  for (const player of players) {
    if (player.role === 'SMALL_BLIND' || player.role === 'DEALER_SMALL_BLIND') {
      blindAmounts[player.id] = smallBlind;
    } else if (player.role === 'BIG_BLIND') {
      blindAmounts[player.id] = bigBlind;
    }
  }

  return blindAmounts;
}

/**
 * Create side pots based on player bets and stacks.
 * @param {Array} players - List of player objects
 * @returns {Array} Array of pot objects
 */
export function createSidePots(players) {
  // TODO: Implement side pot calculation logic
  return [];
}

/**
 * Award the pot(s) to the winner.
 * @param {Array} pots - Array of pot objects
 * @param {string} winnerId - ID of the winning player
 * @param {Array} players - Array of player objects to update stacks
 * @returns {Object} Object with updated players and pots
 */
export function awardPotToWinner(pots, winnerId, players) {
  console.log('🏆 AWARDING POTS TO WINNER:', { winnerId, pots });
  
  let totalWinnings = 0;
  
  // Calculate total winnings from all pots the winner is eligible for
  const updatedPots = pots.map(pot => {
    if (pot.eligiblePlayers.includes(winnerId)) {
      console.log(`🏆 Winner ${winnerId} eligible for pot ${pot.id}: ${pot.amount} chips`);
      totalWinnings += pot.amount;
      return { ...pot, amount: 0, eligiblePlayers: [] }; // Empty the pot
    } else {
      console.log(`🏆 Winner ${winnerId} NOT eligible for pot ${pot.id}: ${pot.amount} chips`);
      return pot;
    }
  });
  
  console.log(`🏆 TOTAL WINNINGS for ${winnerId}: ${totalWinnings} chips`);
  
  // Update the winner's stack
  const updatedPlayers = players.map(player => {
    if (player.id === winnerId) {
      const newStack = Math.round(player.stack + totalWinnings);
      console.log(`🏆 ${player.name} stack: ${player.stack} + ${totalWinnings} = ${newStack}`);
      return {
        ...player,
        stack: newStack,
      };
    }
    return player;
  });
  
  return {
    updatedPlayers,
    updatedPots,
    totalWinnings,
  };
}

/**
 * Award pot(s) to multiple winners with proper Texas Hold'em split pot rules.
 * Each pot can have different winners, and tied players split eligible pots evenly.
 * Remainder chips go to players in earliest position relative to dealer button.
 * 
 * @param {Array} pots - Array of pot objects
 * @param {Array} winnerIds - Array of winning player IDs
 * @param {Array} players - Array of player objects to update stacks
 * @param {Object} playerPositions - Map of playerId to position (for remainder chip distribution)
 * @returns {Object} Object with updated players, pots, and winnings breakdown
 */
export function awardPotsToWinners(pots, winnerIds, players, playerPositions = {}) {
  console.log('🏆 AWARDING POTS TO MULTIPLE WINNERS:', { winnerIds, pots, playerPositions });

  // Every chip that leaves a pot lands here, whether it was won or handed back.
  const payouts = {};
  winnerIds.forEach(id => { payouts[id] = 0; });
  const pay = (id, amount) => { payouts[id] = (payouts[id] || 0) + amount; };

  // Split `amount` across `ids`, giving leftover chips to the earliest positions
  // relative to the button. Shared by the award and the return paths so odd
  // amounts can never be lost to rounding in either one.
  const distribute = (amount, ids, label) => {
    if (ids.length === 0 || amount <= 0) return;

    const baseAmount = Math.floor(amount / ids.length);
    const remainderChips = amount % ids.length;
    const sorted = [...ids].sort((a, b) => (playerPositions[a] || 0) - (playerPositions[b] || 0));

    sorted.forEach((id, i) => {
      const share = baseAmount + (i < remainderChips ? 1 : 0);
      pay(id, share);
      console.log(`🏆 ${id} ${label} ${share} chips`);
    });
  };

  // Any pot whose chips could not be matched to a declared winner. Conserved
  // rather than dropped, but worth surfacing - see ambiguousOrphan below.
  let ambiguousOrphan = false;

  // Process each pot independently
  const updatedPots = pots.map(pot => {
    const eligible = pot.eligiblePlayers || [];
    console.log(`🎯 Processing pot ${pot.id}: ${pot.amount} chips, eligible: [${eligible.join(', ')}]`);

    // Find which winners are eligible for this specific pot
    const eligibleWinners = winnerIds.filter(winnerId => eligible.includes(winnerId));

    if (eligibleWinners.length > 0) {
      distribute(pot.amount, eligibleWinners, `wins from pot ${pot.id}:`);
      return { ...pot, amount: 0, eligiblePlayers: [] };
    }

    // No declared winner can win this pot. Returning `pot` here would strand the
    // chips: callers clear the pots and zero every contribution straight after,
    // so anything left in a pot is destroyed. Hand it back instead.
    if (pot.amount > 0) {
      if (eligible.length > 0) {
        // Contenders who out-bet every winner. Each contributor to a given level
        // put in the identical amount, so an even split returns exactly their own
        // chips.
        if (eligible.length > 1) {
          // Two or more live contenders and no ruling on which of them won it.
          // Splitting conserves the chips but may pay the wrong player.
          ambiguousOrphan = true;
          console.error(
            `🚨 Pot ${pot.id} (${pot.amount}) has ${eligible.length} eligible players but no declared winner. ` +
            `Splitting to conserve chips - the host was never asked who won this pot.`
          );
        }
        distribute(pot.amount, eligible, `is returned from pot ${pot.id}:`);
      } else {
        // Only folded players contributed at this level, so it is dead money.
        // It belongs to whoever won the hand.
        console.log(`🎯 Pot ${pot.id} has no live contenders - dead money to the winner(s)`);
        distribute(pot.amount, winnerIds, `takes dead money from pot ${pot.id}:`);
      }
    }

    return { ...pot, amount: 0, eligiblePlayers: [] };
  });

  // Update player stacks with everything they are owed
  const updatedPlayers = players.map(player => {
    const amount = payouts[player.id] || 0;
    if (amount > 0) {
      const newStack = Math.round(player.stack + amount);
      console.log(`🏆 ${player.name} stack: ${player.stack} + ${amount} = ${newStack}`);
      return {
        ...player,
        stack: newStack,
      };
    }
    return player;
  });

  // Winnings are what the declared winners took; refunds are chips handed back to
  // everyone else. Kept apart so the UI can word them differently.
  const playerWinnings = {};
  winnerIds.forEach(id => { playerWinnings[id] = payouts[id] || 0; });
  const refunds = {};
  Object.entries(payouts).forEach(([id, amount]) => {
    if (!winnerIds.includes(id) && amount > 0) refunds[id] = amount;
  });

  const totalWinnings = Object.values(payouts).reduce((sum, amount) => sum + amount, 0);

  // Chips in must equal chips out. A mismatch means a pot was miscounted upstream;
  // say so loudly rather than letting the table quietly gain or lose money.
  const totalPotAmount = pots.reduce((sum, pot) => sum + (pot.amount || 0), 0);
  if (totalWinnings !== totalPotAmount) {
    console.error(`🚨 CHIP CONSERVATION FAILURE: pots held ${totalPotAmount} but ${totalWinnings} was paid out`);
  }

  console.log('🏆 FINAL WINNINGS BREAKDOWN:', playerWinnings);
  if (Object.keys(refunds).length > 0) console.log('↩️ RETURNED:', refunds);
  console.log(`🏆 TOTAL AWARDED: ${totalWinnings} chips`);

  return {
    updatedPlayers,
    updatedPots,
    totalWinnings,
    playerWinnings, // Individual breakdown for UI display
    refunds,        // Chips handed back to players who were not declared winners
    ambiguousOrphan,
  };
}

/**
 * Check if a hand is complete and determine winner(s).
 * @param {Object} gameState - Current game state
 * @returns {Object} Winner info or null if hand not complete
 */
export function checkForHandCompletion(gameState) {
  const { players, stage, currentTurnPlayerId } = gameState;
  const activePlayers = players.filter(p => !p.hasFolded && p.isActive);
  
  // Case 1: Only one player remains (everyone else folded)
  if (activePlayers.length === 1) {
    return {
      isComplete: true,
      winType: 'FOLD',
      winner: activePlayers[0],
      message: `${activePlayers[0].name} wins! All other players folded.`,
    };
  }
  
  // Case 2: Reached showdown stage with multiple players
  if (stage === 'SHOWDOWN' && activePlayers.length > 1) {
    // For now, we'll need manual winner declaration at showdown
    // In a full implementation, this would compare hands
    return {
      isComplete: true,
      winType: 'SHOWDOWN',
      winner: null, // Needs manual declaration
      activePlayers: activePlayers,
      message: `Showdown! ${activePlayers.length} players remain. Host must declare winner.`,
    };
  }
  
  // Case 3: Betting round complete, advance to next stage
  if (currentTurnPlayerId === null && stage !== 'SHOWDOWN') {
    return {
      isComplete: false,
      shouldAdvanceStage: true,
    };
  }
  
  return {
    isComplete: false,
    shouldAdvanceStage: false,
  };
}

/**
 * Reset game state for a new hand.
 * @param {Object} gameState - Current game state
 * @returns {Object} Updated game state for new hand
 */
export function resetForNewHand(gameState) {
  const { players, dealerId } = gameState;
  
  // Rotate roles for next hand
  const { roleAssignments, nextDealerId } = rotateRoles(players, dealerId);
  
  // Reset player states for new hand
  const resetPlayers = players.map(player => ({
    ...player,
    role: roleAssignments[player.id] || 'PLAYER',
    currentRoundBet: 0,
    totalPotContribution: 0, // Reset for new hand
    hasFolded: false,
    isActive: player.stack > 0, // Only active if they have chips
    isAllIn: false,
    hasActedThisRound: false, // Reset for new betting round
  }));
  
  // Keep ALL players in the game, but only players with chips can participate in pots
  const playersWithChips = resetPlayers.filter(p => p.stack > 0);
  
  return {
    ...gameState,
    players: resetPlayers, // Keep ALL players, including busted ones
    dealerId: nextDealerId,
    stage: 'PRE_FLOP',
    currentTurnPlayerId: null,
    pots: [{ id: 'main', amount: 0, eligiblePlayers: playersWithChips.map(p => p.id), isMainPot: true }],
    canAdvanceStage: false,
    nextStage: null,
    nextStageName: null,
    stageProgress: 0
  };
}

/**
 * Rotate dealer, small blind, and big blind roles.
 * @param {Array} players - List of player objects
 * @param {string} dealerId - Current dealer's ID
 * @returns {Object} New roles assignment
 */
export function rotateRoles(players, dealerId) {
  // Use the assignRoles function with current dealer to get next roles
  return assignRoles(players, dealerId);
}

/**
 * Advance to the next stage of the hand.
 * @param {string} currentStage - Current stage (e.g., PRE_FLOP, FLOP, etc.)
 * @returns {string} Next stage
 */
export function advanceStage(currentStage) {
  const stages = ['PRE_FLOP', 'FLOP', 'TURN', 'RIVER', 'SHOWDOWN'];
  const currentIndex = stages.indexOf(currentStage);
  
  if (currentIndex === -1 || currentIndex === stages.length - 1) {
    return currentStage; // Invalid stage or already at showdown
  }
  
  return stages[currentIndex + 1];
}

/**
 * Get the first player to act for post-flop stages (FLOP, TURN, RIVER).
 * In Texas Hold'em, post-flop action starts with the first active player left of the dealer.
 * @param {Array} players - List of player objects with roles assigned
 * @param {string} dealerId - Current dealer's ID
 * @returns {string|null} Player ID of first to act post-flop, or null if error
 */
export function getFirstToActPostFlop(players, dealerId) {
  if (!dealerId || players.length < 2) return null;

  const dealerIndex = players.findIndex(p => p.id === dealerId);
  if (dealerIndex === -1) return null;

  // Find first active player left of dealer (clockwise) who has chips to act with
  for (let i = 1; i <= players.length; i++) {
    const playerIndex = (dealerIndex + i) % players.length;
    const player = players[playerIndex];
    if (player.isActive && !player.hasFolded && !player.isAllIn && player.stack > 0) {
      return player.id;
    }
  }

  return null;
}

/**
 * Check if no more betting action is possible due to all-in scenarios.
 * This handles cases where betting is capped by an all-in player and all others have responded.
 * @param {Array} players - List of player objects
 * @returns {Object} Object with noBetting boolean and reason string
 */
export function isNoBettingActionPossible(players) {
  const activePlayers = players.filter(p => !p.hasFolded && p.isActive);
  
  if (activePlayers.length <= 1) {
    return { noBetting: true, reason: 'ONLY_ONE_PLAYER' };
  }
  
  const currentHighestBet = Math.max(...players.map(p => p.currentRoundBet || 0), 0);
  
  // Find who set the highest bet
  const highestBettor = players.find(p => p.currentRoundBet === currentHighestBet);
  
  // If the highest bet was set by an all-in player, betting is capped
  if (highestBettor && highestBettor.isAllIn && currentHighestBet > 0) {
    console.log(`🔍 Checking betting cap: ${highestBettor.name} is all-in with bet ${currentHighestBet}`);
    
    // Check if all other active players have either:
    // 1. Matched the capped bet and acted, OR
    // 2. Are also all-in, OR  
    // 3. Have folded (already filtered out)
    const allOthersResponded = activePlayers.every(player => {
      if (player.id === highestBettor.id) return true; // Skip the all-in player
      
      const hasMatchedAndActed = player.currentRoundBet === currentHighestBet && player.hasActedThisRound;
      const isAlsoAllIn = player.isAllIn;
      
      console.log(`🔍 Player ${player.name}: bet=${player.currentRoundBet}, acted=${player.hasActedThisRound}, allIn=${isAlsoAllIn}, matched=${hasMatchedAndActed}`);
      
      return hasMatchedAndActed || isAlsoAllIn;
    });
    
    if (allOthersResponded) {
      console.log('🔍 All players have responded to all-in bet - betting capped');
      return { noBetting: true, reason: 'BETTING_CAPPED_BY_ALL_IN' };
    }
  }
  
  // Check if everyone who can act has acted and matched (traditional all-in scenario)
  const playersWhoCanAct = activePlayers.filter(p => !p.isAllIn && p.stack > 0);
  
  if (playersWhoCanAct.length === 0) {
    console.log('🔍 All remaining players are all-in');
    return { noBetting: true, reason: 'ALL_REMAINING_ALL_IN' };
  }
  
  // Check if only one player can act and they've already responded appropriately
  if (playersWhoCanAct.length === 1) {
    const lastPlayer = playersWhoCanAct[0];
    if (lastPlayer.currentRoundBet === currentHighestBet && lastPlayer.hasActedThisRound) {
      console.log(`🔍 Last acting player ${lastPlayer.name} has responded - no more action possible`);
      return { noBetting: true, reason: 'LAST_PLAYER_RESPONDED' };
    }
  }
  
  return { noBetting: false, reason: 'BETTING_CONTINUES' };
}

/**
 * Check if the current betting round is complete according to Texas Hold'em rules.
 * A round is complete when all active players have had equal opportunity to act.
 * @param {Array} players - List of player objects
 * @param {string|null} currentTurnPlayerId - ID of player whose turn it is
 * @param {string} stage - Current game stage (PRE_FLOP, FLOP, TURN, RIVER)
 * @param {number} bigBlindAmount - Big blind amount to check against
 * @returns {boolean} True if betting round is complete
 */
export function isBettingRoundComplete(players, currentTurnPlayerId, stage, bigBlindAmount = 20) {
  // First check if no more betting action is possible (all-in scenarios)
  const allInCheck = isNoBettingActionPossible(players);
  if (allInCheck.noBetting) {
    console.log(`🔍 Round complete: ${allInCheck.reason}`);
    return true;
  }
  
  const activePlayers = players.filter(p => !p.hasFolded && p.isActive);
  
  // Only one player left = round complete
  if (activePlayers.length <= 1) {
    console.log('🔍 Round complete: Only one player left');
    return true;
  }
  
  const currentHighestBet = Math.max(...players.map(p => p.currentRoundBet || 0), 0);
  // Only players who are not all-in AND have chips can act (chipless players with stack=0 cannot act)
  const playersWhoCanAct = activePlayers.filter(p => !p.isAllIn && p.stack > 0);
  
  // Use hasActedThisRound flag system for both PRE-FLOP and POST-FLOP
  // This ensures proper betting completion in all scenarios including big blind raises
  
  // Check if all players who can act have:
  // 1. Acted this round (hasActedThisRound = true)
  // 2. Called the current bet (currentRoundBet = currentHighestBet)
  const allPlayersHaveActedAndCalled = playersWhoCanAct.every(p => 
    p.hasActedThisRound && p.currentRoundBet === currentHighestBet
  );
  
  // Debug logging
  console.log('🔍 Betting round check (UNIFIED LOGIC):', {
    stage,
    currentHighestBet,
    playersWhoCanAct: playersWhoCanAct.map(p => ({ 
      name: p.name, 
      currentRoundBet: p.currentRoundBet, 
      hasActedThisRound: p.hasActedThisRound,
      role: p.role 
    })),
    allPlayersHaveActedAndCalled,
    currentTurnPlayerId
  });
  
  // If not all players have acted and called the current bet, round is NOT complete
  if (!allPlayersHaveActedAndCalled) {
    console.log('🔍 Round NOT complete: Not all players have acted and called current bet');
    return false;
  }
  
  console.log('🔍 Round complete: All players have acted and called current bet');
  return true;
  
  // NEW: Check for betting cap scenarios (all-in player sets the betting limit)
  // This handles cases where an all-in player caps the betting but other players have chips left
  if (currentHighestBet > 0) {
    // Find who set the highest bet
    const highestBettingPlayer = players.find(p => p.currentRoundBet === currentHighestBet);
    
    if (highestBettingPlayer && highestBettingPlayer.isAllIn) {
      // The highest bet was set by an all-in player - check if betting is capped
      const allOthersMatchedAndActed = playersWhoCanAct.every(p => 
        p.currentRoundBet === currentHighestBet && p.hasActedThisRound
      );
      
      if (allOthersMatchedAndActed) {
        console.log('🔍 Round complete: Betting capped by all-in player, all others have matched and acted');
        return true;
      }
      
      console.log('🔍 Round NOT complete: Betting capped by all-in player, but not all others have matched/acted');
    }
  }
  
  // If we have no next player, round is complete
  if (!currentTurnPlayerId) {
    console.log('🔍 Round complete: No next player');
    return true;
  }
  
  console.log('🔍 Round NOT complete: Players still need to act');
  return false;
}

/**
 * Get the minimum raise amount according to Texas Hold'em rules.
 * Minimum raise must be at least the size of the previous bet or raise.
 * @param {Array} players - List of player objects
 * @param {number} bigBlind - Big blind amount (minimum bet size)
 * @param {Object} gameState - Current game state with lastRaiseAmount
 * @returns {number} Minimum raise amount
 */
export function getMinimumRaise(players, bigBlind, gameState = {}) {
  const currentHighestBet = Math.max(...players.map(p => p.currentRoundBet || 0), 0);
  const lastRaiseAmount = gameState.lastRaiseAmount || 0;
  
  if (currentHighestBet === 0) {
    // No bet yet in this betting round, minimum bet is big blind
    return bigBlind;
  }
  
  // Pre-flop: if only blinds are posted, minimum raise is big blind
  if (gameState.stage === 'PRE_FLOP') {
    // Check if we only have blind bets
    const nonBlindPlayers = players.filter(p => 
      p.role !== 'SMALL_BLIND' && 
      p.role !== 'BIG_BLIND' && 
      p.role !== 'DEALER_SMALL_BLIND' &&
      (p.currentRoundBet || 0) > 0
    );
    
    if (nonBlindPlayers.length === 0) {
      // Only blinds posted, minimum raise is big blind
      return bigBlind;
    }
  }
  
  // Post-flop or when there are actual bets/raises:
  // If we have a tracked last raise amount from THIS betting round, use it
  if (lastRaiseAmount > 0) {
    return lastRaiseAmount;
  }
  
  // Fallback: minimum raise is big blind
  return bigBlind;
}

/**
 * Get the maximum effective bet amount based on opponents' stack sizes.
 * In poker, you can only win as much as you can lose from each opponent.
 * @param {Array} players - List of player objects
 * @param {string} actingPlayerId - ID of the player making the bet
 * @returns {number} Maximum effective total bet amount
 */
export function getMaximumRaise(players, actingPlayerId) {
  const actingPlayer = players.find(p => p.id === actingPlayerId);
  if (!actingPlayer) {
    console.log('🚫 Acting player not found');
    return 0;
  }

  // Get all opponents who haven't folded and are still active
  const activeOpponents = players.filter(p => 
    p.id !== actingPlayerId && 
    !p.hasFolded && 
    p.isActive
  );

  if (activeOpponents.length === 0) {
    console.log('🚫 No active opponents found');
    return actingPlayer.currentRoundBet + actingPlayer.stack; // Can bet everything if no opponents
  }

  // Calculate maximum effective bet based on opponents' ability to call
  let maxEffectiveBet = actingPlayer.currentRoundBet; // Start with current bet

  for (const opponent of activeOpponents) {
    // Each opponent can contribute at most: their current bet + their remaining stack
    const opponentMaxContribution = (opponent.currentRoundBet || 0) + (opponent.stack || 0);
    
    // The acting player can only win this much from this opponent
    // So their effective bet against this opponent is capped at this amount
    maxEffectiveBet = Math.max(maxEffectiveBet, opponentMaxContribution);
  }

  // The acting player's actual maximum bet is limited by their own chips
  const playerMaxBet = (actingPlayer.currentRoundBet || 0) + (actingPlayer.stack || 0);
  
  // Return the smaller of: what opponents can match vs what player can bet
  const finalMaxBet = Math.min(maxEffectiveBet, playerMaxBet);
  
  console.log(`💰 MAXIMUM RAISE CALCULATION for ${actingPlayer.name}:`);
  console.log(`   Acting player: ${actingPlayer.currentRoundBet || 0} bet + ${actingPlayer.stack || 0} stack = ${playerMaxBet} max`);
  console.log(`   Active opponents:`, activeOpponents.map(p => 
    `${p.name}: ${p.currentRoundBet || 0} bet + ${p.stack || 0} stack = ${(p.currentRoundBet || 0) + (p.stack || 0)} max`
  ));
  console.log(`   Effective maximum bet: ${finalMaxBet}`);
  
  return finalMaxBet;
}

/**
 * Determine if automatic stage progression should occur.
 * @param {Object} gameState - Current game state
 * @returns {Object} Result indicating if stage should advance and what action to take
 */
export function shouldAutoAdvanceStage(gameState) {
  const { players, stage, currentTurnPlayerId } = gameState;
  const activePlayers = players.filter(p => !p.hasFolded && p.isActive);
  
  // Only one player left - hand is over, award pot
  if (activePlayers.length === 1) {
    return {
      shouldAdvance: false,
      shouldEndHand: true,
      winner: activePlayers[0],
      winType: 'FOLD'
    };
  }
  
  // Betting round is complete
  if (isBettingRoundComplete(players, currentTurnPlayerId, stage, 20)) {
    if (stage === 'RIVER') {
      // After river betting, go to showdown
      return {
        shouldAdvance: true,
        nextStage: 'SHOWDOWN',
        shouldEndHand: true,
        winType: 'SHOWDOWN'
      };
    } else if (stage !== 'SHOWDOWN') {
      // Advance to next stage
      return {
        shouldAdvance: true,
        nextStage: advanceStage(stage),
        shouldEndHand: false
      };
    }
  }
  
  return {
    shouldAdvance: false,
    shouldEndHand: false
  };
}

/**
 * Calculate side pots based on player total pot contributions.
 * This function determines how much each player should contribute to main pot vs side pots.
 * @param {Array} players - List of player objects with totalPotContribution property
 * @returns {Array} Array of pot objects with amounts and eligible players
 */
export function calculateSidePots(players) {
  console.log('🎯 CALCULATING SIDE POTS (NEW SYSTEM):', {
    players: players.map(p => ({ 
      name: p.name, 
      totalPotContribution: p.totalPotContribution,
      currentRoundBet: p.currentRoundBet,
      hasFolded: p.hasFolded,
      isActive: p.isActive 
    }))
  });
  
  // Include ALL players who contributed chips (even if folded) - their chips stay in the pot
  const playersWithContributions = players.filter(p => {
    const contribution = p.totalPotContribution || 0;
    return contribution > 0;
  }).map(p => ({
    ...p,
    totalContribution: p.totalPotContribution || 0
  }));

  if (playersWithContributions.length === 0) {
    console.log('🎯 No players with contributions');
    return [{ id: 'main', amount: 0, eligiblePlayers: [], isMainPot: true }];
  }

  console.log('🎯 Players with contributions (including folded):', playersWithContributions.map(p => ({ 
    name: p.name, 
    totalContribution: p.totalContribution,
    hasFolded: p.hasFolded
  })));

  // Sort players by total contribution (ascending)
  const sortedPlayers = [...playersWithContributions].sort((a, b) => a.totalContribution - b.totalContribution);
  
  const pots = [];
  let previousContributionLevel = 0;
  
  // Create pots for each contribution level
  for (let i = 0; i < sortedPlayers.length; i++) {
    const currentContributionLevel = sortedPlayers[i].totalContribution;
    const contributionDifference = currentContributionLevel - previousContributionLevel;
    
    if (contributionDifference > 0) {
      // Players eligible for this pot: those who contributed at this level or more AND haven't folded
      const eligiblePlayers = sortedPlayers.slice(i).filter(p => !p.hasFolded).map(p => p.id);
      
      // Pot amount: contribution difference × number of ALL players who contributed at this level (folded or not)
      const contributingPlayersAtThisLevel = sortedPlayers.slice(i).length;
      const potAmount = contributionDifference * contributingPlayersAtThisLevel;
      
      const pot = {
        id: i === 0 ? 'main' : `side-${i}`,
        amount: Math.round(potAmount),
        eligiblePlayers: eligiblePlayers,
        isMainPot: i === 0
      };
      
      console.log(`🎯 Creating pot ${pot.id}: ${potAmount} chips (${contributingPlayersAtThisLevel} contributed, ${eligiblePlayers.length} eligible):`, eligiblePlayers);
      pots.push(pot);
    }
    
    previousContributionLevel = currentContributionLevel;
  }

  // Verify math - total pot should equal ALL contributions (including folded players)
  const totalPotAmount = pots.reduce((sum, pot) => sum + pot.amount, 0);
  const totalContributions = playersWithContributions.reduce((sum, p) => sum + p.totalContribution, 0);
  console.log(`🎯 SIDE POT MATH CHECK: Total pots = ${totalPotAmount}, Total contributions = ${totalContributions}, Match = ${totalPotAmount === totalContributions ? '✅' : '❌'}`);

  return pots;
}

/**
 * Find the uncalled portion of the largest wager, if there is one.
 *
 * A bet nobody matched was never at risk, so it is not a pot and must go back to
 * whoever put it out. That happens when exactly one player sits above every other
 * contributor: everyone else has folded or is all-in for less, so the excess over
 * the next-highest contribution can never be won by anyone but its owner.
 *
 * Deliberately level-based, and narrower than "any pot with a single eligible
 * player". A lower-level pot with one eligible contender holds folded players'
 * dead money — that player *wins* those chips, which is not the same thing as
 * getting their own unmatched chips handed back.
 *
 * @param {Array} players - Players carrying totalPotContribution
 * @returns {Object|null} { playerId, amount } or null when every wager was matched
 */
export function calculateUncalledBet(players) {
  // Folded players count here: their chips are matched money and set the level
  // that the top contributor's bet has to clear to be considered called.
  const contributions = players
    .map(p => ({ id: p.id, amount: p.totalPotContribution || 0 }))
    .filter(c => c.amount > 0)
    .sort((a, b) => b.amount - a.amount);

  if (contributions.length === 0) return null;

  const highest = contributions[0].amount;
  const secondHighest = contributions.length > 1 ? contributions[1].amount : 0;

  if (highest <= secondHighest) return null; // top level is shared - everything was called

  return { playerId: contributions[0].id, amount: highest - secondHighest };
}

/**
 * Move any uncalled wager back from the pot to the player who made it.
 *
 * One pass is enough: once the top contribution is capped at the second-highest,
 * at least two players share the top level and nothing else is unmatched.
 *
 * @param {Array} players - Players carrying totalPotContribution
 * @returns {Object} { players, refund } - refund is null when nothing was returned
 */
export function returnUncalledBet(players) {
  const refund = calculateUncalledBet(players);
  if (!refund) return { players, refund: null };

  const updatedPlayers = players.map(p => {
    if (p.id !== refund.playerId) return p;

    const newStack = Math.round((p.stack || 0) + refund.amount);
    console.log(`↩️ Returning uncalled ${refund.amount} to ${p.name}: stack ${p.stack} → ${newStack}`);
    return {
      ...p,
      stack: newStack,
      totalPotContribution: (p.totalPotContribution || 0) - refund.amount,
      // Getting chips back un-does the all-in: a player with chips behind is not
      // all-in, whatever they were a moment ago.
      isAllIn: newStack > 0 ? false : p.isAllIn
    };
  });

  return { players: updatedPlayers, refund };
}

/**
 * Calculate side pots from current game state by using actual pot amounts.
 * This is the primary function to use during gameplay.
 * @param {Object} gameState - Current game state with players and pots
 * @returns {Array} Array of properly calculated side pots
 */
export function calculateSidePotsFromState(gameState) {
  // Always use totalPotContribution-based calculation for accuracy
  // The totalPotContribution tracks exactly how much each player has put into the pot
  return calculateSidePots(gameState.players);
} 

/**
 * Debug function to test pot calculations
 * Available globally as window.debugPots() when app is running
 */
export function debugPots() {
  console.log('🎰 === POT SYSTEM DEBUG ===');
  
  // Test scenario: 3 players with different contributions
  const testPlayers = [
    { 
      id: 'player1', 
      name: 'Alice', 
      totalPotContribution: 5000, // $50.00 in cents
      currentRoundBet: 0,
      hasFolded: false,
      isActive: true,
      isAllIn: true 
    },
    { 
      id: 'player2', 
      name: 'Bob', 
      totalPotContribution: 110000, // $1100.00 in cents  
      currentRoundBet: 0,
      hasFolded: false,
      isActive: true,
      isAllIn: false
    },
    { 
      id: 'player3', 
      name: 'Charlie', 
      totalPotContribution: 110000, // $1100.00 in cents
      currentRoundBet: 0, 
      hasFolded: false,
      isActive: true,
      isAllIn: false
    }
  ];
  
  console.log('🎯 Test scenario: Alice all-in $1000, Bob and Charlie each contributed $1100');
  console.log('🎯 Expected: Main pot $3000 (Alice eligible), Side pot $200 (Alice NOT eligible)');
  
  const sidePots = calculateSidePots(testPlayers);
  
  console.log('🎯 Calculated side pots:', sidePots);
  
  // Test each player winning
  ['player1', 'player2', 'player3'].forEach(winnerId => {
    const winnerName = testPlayers.find(p => p.id === winnerId).name;
    console.log(`\n🏆 Testing ${winnerName} winning:`);
    
    const { totalWinnings } = awardPotToWinner(sidePots, winnerId, testPlayers);
    console.log(`🏆 ${winnerName} would win: $${(totalWinnings / 100).toFixed(2)}`);
  });
  
  console.log('\n🧮 Pot system debug complete!');
}

/**
 * SETTLEMENT SYSTEM - Calculate who owes money to whom at end of game
 */

/**
 * Calculate settlement transactions based on player results
 * @param {Array} players - Array of players with final stacks and investment data
 * @returns {Object} Settlement information with transactions and summary
 */
export function calculateSettlements(players) {
  console.log('💰 CALCULATING SETTLEMENTS...');

  // Calculate each player's profit/loss
  const playersWithResults = players.map(player => {
    const finalChips = player.stack || 0;
    const totalInvestment = player.totalInvestment || 0;
    const netResult = finalChips - totalInvestment;

    return {
      ...player,
      finalChips,
      totalInvestment,
      netResult, // Positive = profit, negative = loss
      isWinner: netResult > 0,
      isLoser: netResult < 0
    };
  });

  // Separate winners and losers
  const winners = playersWithResults.filter(p => p.netResult > 0);
  const losers = playersWithResults.filter(p => p.netResult < 0);

  // Calculate transactions using simple greedy algorithm
  const transactions = [];
  const winnersQueue = winners.map(p => ({ ...p, amountOwed: p.netResult }));
  const losersQueue = losers.map(p => ({ ...p, amountToCollect: Math.abs(p.netResult) }));

  while (winnersQueue.length > 0 && losersQueue.length > 0) {
    // Sort by amount (descending)
    winnersQueue.sort((a, b) => b.amountOwed - a.amountOwed);
    losersQueue.sort((a, b) => b.amountToCollect - a.amountToCollect);

    const winner = winnersQueue[0];
    const loser = losersQueue[0];

    // Calculate settlement amount
    const settlementAmount = Math.min(winner.amountOwed, loser.amountToCollect);

    if (settlementAmount > 0) {
      transactions.push({
        from: loser.name,
        to: winner.name,
        amount: settlementAmount,
        fromPlayerId: loser.id,
        toPlayerId: winner.id
      });

      // Update remaining amounts
      winner.amountOwed -= settlementAmount;
      loser.amountToCollect -= settlementAmount;
    }

    // Remove settled players from queues
    if (winner.amountOwed <= 0) winnersQueue.shift();
    if (loser.amountToCollect <= 0) losersQueue.shift();
  }

  return {
    transactions,
    playersWithResults,
    hasSettlements: transactions.length > 0
  };
} 